import { AssignmentType, RowItem, TimeOffBlockType } from '../../types';
import { PlannerDataSlice, PlannerSliceCreator } from '../types';

/**
 * The planner's loaded data: rows plus per-row assignment/time-off maps (keyed by
 * row id). Fresh snapshots MUST land via setPlannerData (atomic) — see the
 * interface doc in types.ts for why split setters cause blank assignment bars.
 */
export const createPlannerDataSlice: PlannerSliceCreator<PlannerDataSlice> = set => ({
  rows: [],
  assignments: new Map(),
  timeOffs: new Map(),

  setAssignments: (assignments: Map<string, AssignmentType[]>) => set({ assignments }),
  setTimeOffs: (timeOffs: Map<string, TimeOffBlockType[]>) => set({ timeOffs }),

  setRows: (rows: RowItem[]) =>
    set(state => {
      // Clean up assignments and timeOffs for rows that no longer exist to prevent memory leaks
      const rowIds = new Set(rows.map(r => r.id));
      const newAssignments = new Map(state.assignments);
      const newTimeOffs = new Map(state.timeOffs);

      for (const [rowId] of newAssignments) {
        if (!rowIds.has(rowId)) {
          newAssignments.delete(rowId);
        }
      }

      for (const [rowId] of newTimeOffs) {
        if (!rowIds.has(rowId)) {
          newTimeOffs.delete(rowId);
        }
      }

      return { rows, assignments: newAssignments, timeOffs: newTimeOffs };
    }),

  setPlannerData: ({ rows, assignments, timeOffs }) =>
    // Single atomic set so the store never holds rows without their assignments —
    // see the action's interface doc and planner-member.mapper.ts for why.
    set({ rows, assignments, timeOffs }),

  addAssignment: (assignment: AssignmentType, rowId: string) =>
    set(state => {
      const newAssignments = new Map(state.assignments);
      newAssignments.set(rowId, [...(newAssignments.get(rowId) || []), assignment]);
      return { assignments: newAssignments };
    }),

  removeAssignment: (assignmentId: number, rowId: string) =>
    set(state => {
      const newAssignments = new Map(state.assignments);
      const assignmentsList = newAssignments.get(rowId) || [];
      newAssignments.set(
        rowId,
        assignmentsList.filter(item => item.id !== assignmentId)
      );
      return { assignments: newAssignments };
    }),

  updateAssignment: (rowId: string, assignment: AssignmentType) =>
    set(state => {
      const newAssignments = new Map(state.assignments);
      const updatedList =
        newAssignments
          .get(rowId)
          ?.map(p => (p.id === assignment.id ? { ...p, ...assignment } : p)) || [];
      newAssignments.set(rowId, updatedList);
      return { assignments: newAssignments };
    }),
});
